import { httpClient, toClientApiError } from "./httpClient";

async function wrap<T>(fn: () => Promise<T>): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    throw toClientApiError(error);
  }
}

export type NavItem = {
  key: string;
  labelHe: string;
  path: string;
  permissionKey?: string;
  order: number;
  moduleId?: string;
  parentKey?: string;
  featureKey?: string;
  children?: NavItem[];
};

export const shellApi = {
  listGrantedPermissions(): Promise<string[]> {
    return wrap(async () => {
      const res = await httpClient.get<{ keys: string[] }>("/permissions/granted");
      return res.data.keys;
    });
  },

  listNavigation(): Promise<NavItem[]> {
    return wrap(async () => {
      const res = await httpClient.get<{ items: NavItem[] }>("/navigation");
      return res.data.items;
    });
  },

  getDashboardSummary(): Promise<{
    openTasks: number;
    overdueTasks: number;
    upcomingMeetings: number;
    appBuildVersion: string | null;
    finance: {
      activeSubscriptions: number;
      debtorCustomers: number;
      openDebtAgorot: number;
      creditAgorot: number;
      paymentsThisMonthAgorot: number;
    };
    forumDashboard: ForumDashboardSummary;
  }> {
    return wrap(async () => {
      const res = await httpClient.get<{
        item: {
          openTasks: number;
          overdueTasks: number;
          upcomingMeetings: number;
          appBuildVersion: string | null;
          finance: {
            activeSubscriptions: number;
            debtorCustomers: number;
            openDebtAgorot: number;
            creditAgorot: number;
            paymentsThisMonthAgorot: number;
          };
          forumDashboard: ForumDashboardSummary;
        };
      }>("/dashboard/summary");
      return res.data.item;
    });
  },

  getCategoryPeriodReport(from: string, to: string): Promise<CategoryPeriodReport> {
    return wrap(async () => {
      const res = await httpClient.get<{ item: CategoryPeriodReport }>(
        "/dashboard/category-period-report",
        { params: { from, to } },
      );
      return res.data.item;
    });
  },

  getForumDashboardDetail(
    kind: ForumDashboardDetailKind,
  ): Promise<ForumDashboardDetail> {
    return wrap(async () => {
      const res = await httpClient.get<{ item: ForumDashboardDetail }>(
        "/dashboard/forum-detail",
        { params: { kind } },
      );
      return res.data.item;
    });
  },

  listTasks() {
    return wrap(async () => {
      const res = await httpClient.get<{ items: TaskDto[] }>("/tasks");
      return res.data.items;
    });
  },

  createTask(body: Partial<TaskDto> & { title: string }) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: TaskDto }>("/tasks", body);
      return res.data.item;
    });
  },

  updateTask(id: string, body: Partial<TaskDto>) {
    return wrap(async () => {
      const res = await httpClient.patch<{ item: TaskDto }>(`/tasks/${id}`, body);
      return res.data.item;
    });
  },

  deleteTask(id: string) {
    return wrap(async () => {
      await httpClient.delete(`/tasks/${id}`);
    });
  },

  getPersonalArea() {
    return wrap(async () => {
      const res = await httpClient.get<{ item: PersonalAreaDto }>("/personal-area");
      return res.data.item;
    });
  },

  updatePersonalArea(body: Record<string, unknown>) {
    return wrap(async () => {
      const res = await httpClient.patch<{ item: PersonalAreaDto }>("/personal-area", body);
      return res.data.item;
    });
  },

  listMailLog(params?: {
    relatedEntityType?: string;
    relatedEntityId?: string;
  }) {
    return wrap(async () => {
      const res = await httpClient.get<{ items: MailLogDto[] }>("/mail-log", {
        params,
      });
      return res.data.items;
    });
  },

  updateMailNotes(id: string, notes: string) {
    return wrap(async () => {
      const res = await httpClient.patch<{ item: MailLogDto }>(`/mail-log/${id}/notes`, {
        notes,
      });
      return res.data.item;
    });
  },

  listForms() {
    return wrap(async () => {
      const res = await httpClient.get<{ items: FormDto[] }>("/forms");
      return res.data.items;
    });
  },

  createForm(body: {
    name: string;
    description?: string | null;
    schemaJson?: Record<string, unknown>;
    notifyEmails?: string[];
    isAvailable?: boolean;
  }) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: FormDto }>("/forms", body);
      return res.data.item;
    });
  },

  updateForm(
    id: string,
    body: {
      name?: string;
      description?: string | null;
      schemaJson?: Record<string, unknown>;
      notifyEmails?: string[];
      isAvailable?: boolean;
    },
  ) {
    return wrap(async () => {
      const res = await httpClient.patch<{ item: FormDto }>(`/forms/${id}`, body);
      return res.data.item;
    });
  },

  deleteForm(id: string) {
    return wrap(async () => {
      await httpClient.delete(`/forms/${id}`);
    });
  },

  listSubmissions(formId: string) {
    return wrap(async () => {
      const res = await httpClient.get<{ items: FormSubmissionDto[] }>(
        `/forms/${formId}/submissions`,
      );
      return res.data.items;
    });
  },

  listConversations() {
    return wrap(async () => {
      const res = await httpClient.get<{ items: ConversationDto[] }>("/chat/conversations");
      return res.data.items;
    });
  },

  listPendingConversations() {
    return wrap(async () => {
      const res = await httpClient.get<{ items: PendingConversationDto[] }>(
        "/chat/conversations/pending",
      );
      return res.data.items;
    });
  },

  listEligibleChatUsers() {
    return wrap(async () => {
      const res = await httpClient.get<{ items: ChatEligibleUserDto[] }>("/chat/eligible-users");
      return res.data.items;
    });
  },

  createConversation(body: { memberUserIds: string[]; name?: string; isGroup?: boolean }) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: ConversationDto }>("/chat/conversations", body);
      return res.data.item;
    });
  },

  acceptPendingConversation(conversationId: string) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: ConversationDto | null }>(
        `/chat/conversations/${conversationId}/accept`,
        {},
      );
      return res.data.item;
    });
  },

  rejectPendingConversation(conversationId: string) {
    return wrap(async () => {
      await httpClient.post(`/chat/conversations/${conversationId}/reject`, {});
    });
  },

  listConversationMembers(conversationId: string) {
    return wrap(async () => {
      const res = await httpClient.get<{ items: ConversationMemberDto[] }>(
        `/chat/conversations/${conversationId}/members`,
      );
      return res.data.items;
    });
  },

  listChatMessages(conversationId: string) {
    return wrap(async () => {
      const res = await httpClient.get<{ items: ChatMessageDto[] }>(
        `/chat/conversations/${conversationId}/messages`,
      );
      return res.data.items;
    });
  },

  sendChatMessage(
    conversationId: string,
    body: {
      content: string;
      attachmentKey?: string | null;
      attachmentName?: string | null;
      replyToMessageId?: string | null;
    },
  ) {
    return wrap(async () => {
      const res = await httpClient.post<{ item: ChatMessageDto }>(
        `/chat/conversations/${conversationId}/messages`,
        body,
      );
      return res.data.item;
    });
  },

  editChatMessage(conversationId: string, messageId: string, content: string) {
    return wrap(async () => {
      const res = await httpClient.patch<{ item: ChatMessageDto }>(
        `/chat/conversations/${conversationId}/messages/${messageId}`,
        { content },
      );
      return res.data.item;
    });
  },

  deleteChatMessage(conversationId: string, messageId: string) {
    return wrap(async () => {
      await httpClient.delete(`/chat/conversations/${conversationId}/messages/${messageId}`);
    });
  },

  toggleChatReaction(conversationId: string, messageId: string, emoji: string) {
    return wrap(async () => {
      const res = await httpClient.post<{
        item: {
          messageId: string;
          reactions: Array<{ emoji: string; userId: string }>;
          action: string;
          emoji: string;
        };
      }>(`/chat/conversations/${conversationId}/messages/${messageId}/reactions`, { emoji });
      return res.data.item;
    });
  },

  markConversationRead(conversationId: string) {
    return wrap(async () => {
      await httpClient.post(`/chat/conversations/${conversationId}/read`, {});
    });
  },

  markConversationUnread(conversationId: string, messageId: string) {
    return wrap(async () => {
      const res = await httpClient.post<{ unreadCount: number; lastReadAt: string }>(
        `/chat/conversations/${conversationId}/unread`,
        { messageId },
      );
      return res.data;
    });
  },

  setChatMessageTreatment(
    conversationId: string,
    messageId: string,
    status: ChatTreatmentStatus | null,
  ) {
    return wrap(async () => {
      const res = await httpClient.patch<{ item: ChatMessageDto }>(
        `/chat/conversations/${conversationId}/messages/${messageId}/treatment`,
        { status },
      );
      return res.data.item;
    });
  },

  listTreatmentQueue(status?: ChatTreatmentStatus | "all" | null) {
    return wrap(async () => {
      const res = await httpClient.get<{ items: TreatmentQueueItemDto[] }>(
        "/chat/treatment-queue",
        { params: status && status !== "all" ? { status } : undefined },
      );
      return res.data.items;
    });
  },

  leaveConversation(conversationId: string) {
    return wrap(async () => {
      await httpClient.post(`/chat/conversations/${conversationId}/leave`, {});
    });
  },

  getTableLayout(tableKey: string) {
    return wrap(async () => {
      const res = await httpClient.get<{ prefs: Record<string, unknown> }>(
        `/table-layout/${encodeURIComponent(tableKey)}`,
      );
      return res.data.prefs;
    });
  },

  saveTableLayout(tableKey: string, prefs: Record<string, unknown>, shared?: boolean) {
    return wrap(async () => {
      await httpClient.put(`/table-layout/${encodeURIComponent(tableKey)}`, {
        prefs,
        shared: Boolean(shared),
      });
    });
  },

  listFieldPolicies(entityKey: string) {
    return wrap(async () => {
      const res = await httpClient.get<{ items: FieldPolicyDto[] }>(
        `/field-policies/${encodeURIComponent(entityKey)}`,
      );
      return res.data.items;
    });
  },

  setFieldPolicy(entityKey: string, body: {
    roleKey: string;
    fieldKey: string;
    access: "view" | "edit" | "hide";
  }) {
    return wrap(async () => {
      const res = await httpClient.put<{ item: FieldPolicyDto }>(
        `/field-policies/${encodeURIComponent(entityKey)}`,
        body,
      );
      return res.data.item;
    });
  },
};

export type TaskDto = {
  id: string;
  title: string;
  description: string | null;
  status: string;
  priority: string;
  dueAt: string | null;
  assignedToUserId: string | null;
  linkedCustomerId: string | null;
  linkedEntityType?: string | null;
  linkedEntityId?: string | null;
  taskType?: string | null;
  createdAt: string;
  updatedAt: string;
};

export type PersonalAreaDto = {
  user: { id: string; email: string; displayName: string };
  preferences: {
    theme: string;
    uiScale: string;
    profileImageKey: string | null;
    prefs: Record<string, unknown>;
  };
  tenantId: string;
};

export type MailLogDto = {
  id: string;
  recipientEmail: string;
  subject: string;
  status: string;
  emailType: string;
  notes: string | null;
  sentAt: string;
  relatedEntityType?: string | null;
  relatedEntityId?: string | null;
};

export type FormDto = {
  id: string;
  name: string;
  description: string | null;
  publicToken: string;
  isAvailable: boolean;
  schemaJson: Record<string, unknown>;
  notifyEmails: string[];
};

export type FormSubmissionDto = {
  id: string;
  dataJson: Record<string, unknown>;
  createdAt: string;
};

export type ConversationMemberDto = {
  userId: string;
  displayName: string;
  approvalStatus: "pending" | "approved" | "rejected";
};

export type ConversationDto = {
  id: string;
  name: string | null;
  isGroup: boolean;
  updatedAt: string;
  createdAt?: string;
  unreadCount?: number;
  lastMessage?: {
    id: string;
    content: string;
    senderUserId: string;
    createdAt: string;
    attachmentName?: string | null;
  } | null;
  members?: ConversationMemberDto[];
};

export type PendingConversationDto = {
  membershipId: string;
  conversationId: string;
  name: string | null;
  isGroup: boolean;
  invitedAt: string;
  invitedBy: { userId: string; displayName: string } | null;
};

export type ChatEligibleUserDto = {
  userId: string;
  displayName: string;
};

export type ChatReplySnapshotDto = {
  replyToMessageId: string;
  senderName: string;
  contentPreview: string;
  attachmentType?: string | null;
  isDeleted: boolean;
};

export type ChatTreatmentStatus = "flagged" | "in_progress" | "handled";

export type ChatMessageDto = {
  id: string;
  conversationId: string;
  senderUserId: string;
  content: string;
  createdAt: string;
  editedAt?: string | null;
  attachmentKey?: string | null;
  attachmentName?: string | null;
  replyToMessageId?: string | null;
  replySnapshot?: ChatReplySnapshotDto | null;
  treatmentStatus?: ChatTreatmentStatus | null;
  treatmentUpdatedBy?: string | null;
  treatmentUpdatedAt?: string | null;
  reactions?: Array<{ emoji: string; userId: string }>;
  deliveryStatus?: "sent" | "delivered" | "read";
};

export type TreatmentQueueItemDto = {
  messageId: string;
  conversationId: string;
  contentPreview: string;
  senderName: string;
  senderUserId: string;
  createdAt: string;
  treatmentStatus: ChatTreatmentStatus;
  treatmentUpdatedAt: string | null;
  conversationName: string;
  isGroup: boolean;
};

export type FieldPolicyDto = {
  id: string;
  roleKey: string;
  entityKey: string;
  fieldKey: string;
  access: "view" | "edit" | "hide";
};

export type CategoryPeriodReportRow = {
  categoryId: string | null;
  name: string;
  activeSubscriptions: number;
  incomeAgorot: number;
  expenseAgorot: number;
};

export type CategoryPeriodReport = {
  from: string;
  to: string;
  rows: CategoryPeriodReportRow[];
};

export type ForumDashboardTopDebt = {
  customerId: string;
  fundName: string;
  department: string | null;
  balanceAgorot: number;
};

export type ForumDashboardRecentPayment = {
  id: string;
  customerId: string;
  fundName: string;
  department: string | null;
  amountAgorot: number;
  paidAt: string;
  method: string;
  status: string;
};

export type ForumDashboardSummary = {
  year: number;
  annualIncomeAgorot: number;
  annualExpenseAgorot: number;
  openDebtAgorot: number;
  debtorCustomers: number;
  creditAgorot: number;
  creditCustomers: number;
  activeFunds: number;
  activeSubscriptions: number;
  expiringSubscriptions: number;
  incomeByCategory: Array<{
    categoryId: string;
    name: string;
    incomeAgorot: number;
  }>;
  topDebts: ForumDashboardTopDebt[];
  recentPayments: ForumDashboardRecentPayment[];
};

export const EMPTY_FORUM_DASHBOARD: ForumDashboardSummary = {
  year: new Date().getFullYear(),
  annualIncomeAgorot: 0,
  annualExpenseAgorot: 0,
  openDebtAgorot: 0,
  debtorCustomers: 0,
  creditAgorot: 0,
  creditCustomers: 0,
  activeFunds: 0,
  activeSubscriptions: 0,
  expiringSubscriptions: 0,
  incomeByCategory: [],
  topDebts: [],
  recentPayments: [],
};

export type ForumDashboardDetailKind =
  | "open_debts"
  | "credit_balances"
  | "active_funds"
  | "active_subscriptions"
  | "expiring_subscriptions";

export type ForumDebtMonth = {
  period: string;
  chargesAgorot: number;
  paymentsAgorot: number;
  netAgorot: number;
};

export type ForumDebtSubscription = {
  subscriptionId: string;
  title: string;
  categoryName: string | null;
  amountAgorot: number;
  status: string;
  startsOn: string | null;
  endsOn: string | null;
  paymentArrangement: string | null;
  months: ForumDebtMonth[];
};

export type ForumDebtFund = {
  customerId: string;
  fundName: string;
  fundCode: number;
  phone: string | null;
  email: string | null;
  balanceAgorot: number;
  subscriptions: ForumDebtSubscription[];
  unassignedMonths: ForumDebtMonth[];
};

export type ForumActiveFund = {
  customerId: string;
  name: string;
  code: number;
  phone: string | null;
  email: string | null;
  balanceAgorot: number;
  activeSubscriptionCount: number;
};

export type ForumCreditFund = {
  customerId: string;
  fundName: string;
  fundCode: number;
  phone: string | null;
  email: string | null;
  creditAgorot: number;
};

export type ForumSubscriptionDetail = {
  id: string;
  title: string;
  customerId: string;
  fundName: string;
  categoryName: string | null;
  amountAgorot: number;
  status: string;
  startsOn: string | null;
  endsOn: string | null;
  daysUntilEnd: number | null;
  paymentArrangement: string | null;
};

export type ForumDashboardDetail =
  | {
      kind: "open_debts";
      totalDebtAgorot: number;
      debtorCount: number;
      items: ForumDebtFund[];
    }
  | {
      kind: "credit_balances";
      totalCreditAgorot: number;
      creditCount: number;
      items: ForumCreditFund[];
    }
  | {
      kind: "active_funds";
      count: number;
      items: ForumActiveFund[];
    }
  | {
      kind: "active_subscriptions" | "expiring_subscriptions";
      count: number;
      items: ForumSubscriptionDetail[];
    };
